import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpCircle, ArrowDownCircle, Eye } from 'lucide-react';
import { bankAccountApi, accountPayableApi, accountReceivableApi } from '../services/api';
import { BankAccount, BankTransaction, AccountPayable, AccountReceivable } from '../types';
import { formatDateAlt } from '../utils/dateUtils';
import PeriodInput, { periodToStartIso, periodToEndIso, isoToPeriod } from '../components/PeriodInput';
import Modal from '../components/Modal';
import Alert from '../components/Alert';
import AccountPayableForm from '../components/AccountPayableForm';
import AccountReceivableForm from '../components/AccountReceivableForm';
import PageSuggestion from '../components/PageSuggestion';

export const BankStatement: React.FC = () => {
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [transactions, setTransactions] = useState<BankTransaction[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
    const [selectedDetail, setSelectedDetail] = useState<AccountPayable | AccountReceivable | null>(null);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    const defaultPeriod = useMemo(() => isoToPeriod(null), []);
    const [filterStartPeriod, setFilterStartPeriod] = useState<string>(defaultPeriod);
    const [filterEndPeriod, setFilterEndPeriod] = useState<string>(defaultPeriod);

    const accountsForSelect = useMemo(
        () => bankAccounts.filter((acc) => acc.isVault !== true),
        [bankAccounts]
    );

    useEffect(() => {
        const fetchBankAccounts = async () => {
            try {
                const response = await bankAccountApi.getAll();
                const accounts = response.data || [];
                setBankAccounts(accounts);
                const forSelect = accounts.filter((acc) => acc.isVault !== true);
                if (forSelect.length > 0) {
                    setSelectedAccountId(forSelect[0].id);
                }
            } catch (err) {
                setError('Erro ao carregar contas bancárias.');
            }
        };
        fetchBankAccounts();
    }, []);

    const fetchTransactions = async () => {
        if (!selectedAccountId) return;
        setLoading(true);
        setError(null);
        try {
            const toUtcBoundary = (iso: string, endOfDay: boolean) => {
                // Extract date parts from ISO string directly to avoid timezone issues
                const dateStr = iso.split('T')[0]; // Get YYYY-MM-DD part
                const [y, m, day] = dateStr.split('-').map(Number);
                const utc = endOfDay
                    ? new Date(Date.UTC(y, m - 1, day, 23, 59, 59, 999))
                    : new Date(Date.UTC(y, m - 1, day, 0, 0, 0, 0));
                return utc.toISOString();
            };

            const startIso = periodToStartIso(filterStartPeriod);
            const endIso = periodToEndIso(filterEndPeriod);
            const params = {
                occurredAtStart: toUtcBoundary(startIso, false),
                occurredAtEnd: toUtcBoundary(endIso, true),
            };

            const response = await bankAccountApi.getTransactions(selectedAccountId, params);
            setTransactions(response.data || []);
        } catch (err: any) {
            const friendly = err?.response?.data?.translatedMessage || 'Erro ao carregar transações.';
            setError(friendly);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccountId]);

    const handleAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedAccountId(e.target.value);
    };

    const handleViewDetail = async (transaction: BankTransaction) => {
        if (!transaction.sourceId) return;

        setIsDetailModalOpen(true);
        setDetailLoading(true);
        setDetailError(null);
        setSelectedDetail(null);

        try {
            let response;

            // Regra específica para estornos (refund):
            // - refund + debit  => buscar em contas a receber
            // - refund + credit => buscar em contas a pagar
            if (transaction.sourceType === 'refund') {
                if (transaction.type === 'debit') {
                    response = await accountReceivableApi.getById(transaction.sourceId);
                } else {
                    response = await accountPayableApi.getById(transaction.sourceId);
                }
            } else if (transaction.sourceType === 'account_payable') {
                response = await accountPayableApi.getById(transaction.sourceId);
            } else if (transaction.sourceType === 'account_receivable') {
                response = await accountReceivableApi.getById(transaction.sourceId);
            } else {
                // Fallback: usa o tipo da transação se o sourceType não estiver explícito
                if (transaction.type === 'debit') {
                    response = await accountPayableApi.getById(transaction.sourceId);
                } else {
                    response = await accountReceivableApi.getById(transaction.sourceId);
                }
            }

            setSelectedDetail(response.data);
        } catch (err) {
            setDetailError('Erro ao carregar detalhes.');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedDetail(null);
        setDetailError(null);
    };

    const selectedAccount = useMemo(
        () => bankAccounts.find((acc) => acc.id === selectedAccountId),
        [bankAccounts, selectedAccountId]
    );

    // Filter transactions by search term
    const filteredTransactions = useMemo(() => {
        if (!searchTerm.trim()) return transactions;

        const lowerSearch = searchTerm.toLowerCase();
        return transactions.filter((t) => {
            return (
                t.description.toLowerCase().includes(lowerSearch) ||
                t.type.toLowerCase().includes(lowerSearch) ||
                t.sourceType.toLowerCase().includes(lowerSearch) ||
                (t.amount / 100).toFixed(2).includes(lowerSearch)
            );
        });
    }, [transactions, searchTerm]);


    const getTransactionTypeLabel = (type: string): string => {
        switch (type) {
            case 'credit':
                return 'Crédito';
            case 'debit':
                return 'Débito';
            default:
                return type;
        }
    };

    const getTransactionSourceTypeLabel = (sourceType: string): string => {
        switch (sourceType) {
            case 'account_payable':
                return 'Conta a Pagar';
            case 'account_receivable':
                return 'Conta a Receber';
            case 'refund':
                return 'Estorno';
            case 'manual':
                return 'Manual';
            default:
                return sourceType;
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-secondary-900">Extrato Bancário</h1>
                <p className="text-secondary-600">Visualize as transações da conta bancária selecionada</p>
                <PageSuggestion>
                    Consulte o extrato para conferir movimentações, validar saldos e abrir rapidamente o detalhe de cada lançamento ligado a contas a pagar ou a receber.
                </PageSuggestion>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-auto sm:min-w-[180px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Conta Bancária</label>
                        <select
                            className="input-field"
                            value={selectedAccountId}
                            onChange={handleAccountChange}
                        >
                            {accountsForSelect.map((account) => (
                                <option key={account.id} value={account.id}>
                                    {account.name} - {(account.currentBalance / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput label="Data inicial (MM/AAAA)" name="occurredAtStart" value={filterStartPeriod} onChange={setFilterStartPeriod} compact />
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput label="Data final (MM/AAAA)" name="occurredAtEnd" value={filterEndPeriod} onChange={setFilterEndPeriod} compact />
                    </div>
                    <button onClick={fetchTransactions} className="btn-primary w-full sm:w-auto shrink-0 h-[42px]">Filtrar</button>
                </div>
            </div>

            {/* Account summary */}
            {selectedAccount && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-800">Saldo Atual</div>
                    <div className="mt-1 text-xl sm:text-2xl font-bold text-blue-900 break-words">
                        {(selectedAccount.currentBalance / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <input
                    type="text"
                    placeholder="Buscar por descrição, tipo, valor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field w-full"
                />
            </div>

            {/* Transactions Table */}
            {error && (<Alert type="error" title="Não foi possível carregar" message={error} />)}

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-secondary-600">Carregando...</div>
                </div>
            ) : filteredTransactions.length === 0 ? (
                <div className="bg-white border border-secondary-200 rounded-lg p-8 text-center">
                    <p className="text-secondary-600">
                        {transactions.length === 0
                            ? 'Nenhuma transação encontrada para o período selecionado.'
                            : 'Nenhuma transação encontrada com os critérios de busca.'}
                    </p>
                </div>
            ) : (
                <div className="bg-white border border-secondary-200 rounded-lg overflow-hidden">
                    <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-secondary-200">
                        <thead className="bg-secondary-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                    Data
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                    Descrição
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                    Valor
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                    Saldo Após
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                    Ações
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-secondary-200">
                            {filteredTransactions.map((transaction) => (
                                <tr key={transaction.id} className="hover:bg-secondary-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                                        {formatDateAlt(transaction.occurredAt)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-secondary-900">
                                        {transaction.description}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {transaction.type === 'credit' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                <ArrowUpCircle className="w-3.5 h-3.5" />
                                                {getTransactionTypeLabel(transaction.type)}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                <ArrowDownCircle className="w-3.5 h-3.5" />
                                                {getTransactionTypeLabel(transaction.type)}
                                            </span>
                                        )}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${transaction.type === 'credit' ? 'text-green-700' : 'text-red-700'}`}>
                                        {transaction.type === 'credit' ? '+' : '-'} {(transaction.amount / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-secondary-900">
                                        {(transaction.balanceAfter / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                        {transaction.sourceId && (
                                            <button
                                                onClick={() => handleViewDetail(transaction)}
                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                                title="Ver detalhes"
                                            >
                                                <Eye className="w-5 h-5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <div className="md:hidden divide-y divide-secondary-200">
                        {filteredTransactions.map((transaction) => (
                            <div key={transaction.id} className="p-4 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-medium text-secondary-900">{transaction.description}</div>
                                    {transaction.sourceId && (
                                        <button
                                            onClick={() => handleViewDetail(transaction)}
                                            className="text-blue-600 hover:text-blue-800 transition-colors"
                                            title="Ver detalhes"
                                        >
                                            <Eye className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                                <div className="text-xs text-secondary-600">
                                    {formatDateAlt(transaction.occurredAt)} • {getTransactionSourceTypeLabel(transaction.sourceType)}
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${transaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {transaction.type === 'credit' ? <ArrowUpCircle className="w-3.5 h-3.5" /> : <ArrowDownCircle className="w-3.5 h-3.5" />}
                                        {getTransactionTypeLabel(transaction.type)}
                                    </span>
                                    <span className={`text-sm font-semibold ${transaction.type === 'credit' ? 'text-green-700' : 'text-red-700'}`}>
                                        {transaction.type === 'credit' ? '+' : '-'} {(transaction.amount / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </span>
                                </div>
                                <div className="text-sm text-secondary-700">
                                    Saldo após: <span className="font-semibold text-secondary-900">{(transaction.balanceAfter / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            <Modal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                title={selectedDetail && 'paymentDate' in selectedDetail ? 'Visualizar Conta a Pagar' : 'Visualizar Conta a Receber'}
            >
                {detailLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="text-secondary-600">Carregando...</div>
                    </div>
                ) : detailError ? (
                    <Alert type="error" title="Não foi possível carregar" message={detailError} />
                ) : selectedDetail ? (
                    selectedDetail && 'paymentDate' in selectedDetail ? (
                        <AccountPayableForm
                            accountPayable={selectedDetail as AccountPayable}
                            onSubmit={async () => { }}
                            onCancel={handleCloseDetailModal}
                            readOnly={true}
                        />
                    ) : (
                        <AccountReceivableForm
                            accountReceivable={selectedDetail as AccountReceivable}
                            onSubmit={async () => { }}
                            onCancel={handleCloseDetailModal}
                            readOnly={true}
                        />
                    )
                ) : null}
            </Modal>
        </div>
    );
};

export default BankStatement;
